import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropdown } from '../../../../widgets/hooks/useDropdown';
import { Icon } from '../../../../widgets/icon';

import { stringFormat, T, uploadFile } from 'src/shared/helpers';
import ArrowLeftIcon from 'src/shared/icons/arrow-left-solid.svg';
import CloudUploadIcon from 'src/shared/icons/cloud-upload-alt-solid.svg';
import FolderOpenIcon from 'src/shared/icons/folder-open-solid.svg';
import PaperPlaneIcon from 'src/shared/icons/paper-plane-solid.svg';
import PlusIcon from 'src/shared/icons/plus-solid.svg';
import ThGridIcon from 'src/shared/icons/th-solid.svg';
import TimesIcon from 'src/shared/icons/times-solid.svg';
import styled, { keyframes } from 'styled-components';
import { spin } from '../../styles/animations';
import { AdOptions, InputImageData, ToolType, VideoOptions } from '../../types';
import { PromptInput } from '../inputs/promptInput';
import { SettingsPanel } from '../layout/settingsPanel';
import { PresetDropdown, PresetDropdownValue } from '../presets/presetDropdown';
import { getPresetTypesForTool } from '../presets/presetTypeConfig';
import { AiStudioHelpLink, AiStudioHelpRow } from '../shared/aiStudioHelpLink';
import { ImageUploadZones } from '../shared/imageUploadZones';
import { HiddenInput } from '../shared/sharedStyles';
import { ToolEmptyStateImage } from '../shared/ToolEmptyStateImage';

/* ─── Keyframes ───────────────────────────────────────────────────────────── */

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const orb1Drift = keyframes`
    0%   { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
    25%  { transform: translate(-50%, -50%) translate(40px, -30px) scale(1.1); }
    50%  { transform: translate(-50%, -50%) translate(-20px, 50px) scale(0.95); }
    75%  { transform: translate(-50%, -50%) translate(60px, 20px) scale(1.05); }
    100% { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
`;

const orb2Drift = keyframes`
    0%   { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
    33%  { transform: translate(-50%, -50%) translate(-50px, 40px) scale(1.08); }
    66%  { transform: translate(-50%, -50%) translate(30px, -60px) scale(0.92); }
    100% { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
`;

const orb3Drift = keyframes`
    0%   { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
    20%  { transform: translate(-50%, -50%) translate(50px, 30px) scale(0.9); }
    50%  { transform: translate(-50%, -50%) translate(-40px, -20px) scale(1.12); }
    80%  { transform: translate(-50%, -50%) translate(20px, 60px) scale(1.0); }
    100% { transform: translate(-50%, -50%) translate(0px, 0px) scale(1); }
`;

/* ─── Container & orbs ────────────────────────────────────────────────────── */

const Container = styled.div`
    position: relative;
    width: 100%;
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    background: linear-gradient(160deg, #fdfcff 0%, #faf8ff 50%, #fffafc 100%);
    animation: ${fadeIn} 0.3s ease forwards;
`;

const BlurOrb1 = styled.div`
    position: absolute;
    top: 0%;
    left: 22%;
    width: 540px;
    height: 540px;
    border-radius: 50%;
    background: rgba(124, 58, 237, 0.07);
    filter: blur(100px);
    pointer-events: none;
    transform: translate(-50%, -50%);
    animation: ${orb1Drift} 12s ease-in-out infinite;
`;

const BlurOrb2 = styled.div`
    position: absolute;
    top: 100%;
    left: 12%;
    width: 440px;
    height: 440px;
    border-radius: 50%;
    background: rgba(168, 85, 247, 0.06);
    filter: blur(95px);
    pointer-events: none;
    transform: translate(-50%, -50%);
    animation: ${orb2Drift} 16s ease-in-out infinite;
`;

const BlurOrb3 = styled.div`
    position: absolute;
    top: 25%;
    left: 88%;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: rgba(236, 72, 153, 0.06);
    filter: blur(85px);
    pointer-events: none;
    transform: translate(-50%, -50%);
    animation: ${orb3Drift} 14s ease-in-out infinite;
`;

/* ─── Content area ────────────────────────────────────────────────────────── */

const Content = styled.div`
    position: relative;
    z-index: 5;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 80px 40px 40px;
    width: 100%;
    box-sizing: border-box;

    @media (max-height: 720px) {
        padding: 48px 40px 28px;
        gap: 8px;
    }

    @media (max-height: 660px) {
        padding: 32px 40px 20px;
        gap: 6px;
    }
`;

const ToolTitle = styled.h2`
    font-size: 38px;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
    text-align: center;
    color: #1e0a3c;
    white-space: nowrap;

    @media (max-height: 720px) {
        font-size: 30px;
    }

    @media (max-height: 660px) {
        font-size: 24px;
    }
`;

const ToolTitleAccent = styled.span`
    background: linear-gradient(90deg, #7c3aed, #ec4899);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const ToolSubtitle = styled.p`
    font-size: 13px;
    color: rgba(30, 10, 60, 0.58);
    margin: 0;
    line-height: 1.6;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
`;

const EmptyStatePreviewWrapper = styled.div`
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

/* ─── Controls card ───────────────────────────────────────────────────────── */

const ControlsCard = styled.div`
    width: 100%;
    max-width: 860px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid rgb(255, 255, 255);
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.45);
    box-sizing: border-box;
`;

/* ─── Image preview (shown after upload) — mirrors bottomPanel InputPreview ── */

const InputPreviews = styled.div`
    display: flex;
    gap: 8px;
    flex-shrink: 0;
`;

const InputPreviewItem = styled.div`
    position: relative;
    min-height: 58px;
    min-width: 58px;
    max-width: 58px;
    max-height: 58px;
`;

const InputPreviewImage = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.backgroundColor};

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
`;

const InputPreviewRemove = styled.button`
    position: absolute;
    top: 0px;
    right: 0px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover {
        background: #dc2626;
    }

    ${Icon} {
        width: 8px;
        height: 8px;
        fill: currentColor;
    }
`;

const AddMoreButton = styled.button`
    width: 58px;
    height: 58px;
    border-radius: 50%;
    border: 2px dashed ${(props) => props.theme.app.borderColor};
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
        border-color: #3b82f6;
        color: #3b82f6;
    }

    &:disabled {
        cursor: default;
        &:hover {
            border-color: ${(props) => props.theme.app.borderColor};
            color: #9ca3af;
        }
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const InputUploadSpinner = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
`;

const AddMoreMenu = styled.div`
    min-width: 200px;
    padding: 6px;
`;

const AddMoreMenuItem = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: ${(props) => props.theme.app.textColor};
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s ease;
    text-align: left;

    &:hover {
        background: #f3f4f6;
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        fill: currentColor;
        flex-shrink: 0;
    }
`;

/* ─── Presets row ─────────────────────────────────────────────────────────── */

const PresetSelectorsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const PresetDropdownsGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
`;

/* ─── Prompt row (prompt + round generate button) ────────────────────────── */

const PromptRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const RoundGenerateButton = styled.button`
    width: 50px;
    height: 50px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;

    &:hover:not(:disabled) {
        transform: scale(1.05);
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    ${Icon} {
        width: 24px;
        height: 24px;
        fill: currentColor;
    }
`;

/* ─── Scrollable tab bar ──────────────────────────────────────────────────── */

const TabsScrollWrapper = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 4px;
`;

const TabsScroller = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    gap: 6px;
    overflow-x: auto;
    scrollbar-width: none;
    padding: 2px 0;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const TabButton = styled.button<{ $active: boolean }>`
    padding: 8px 16px;
    border: none;
    border-radius: 18px;
    background: ${(p) => (p.$active ? '#000000' : 'rgba(0, 0, 0, 0.06)')};
    color: ${(p) => (p.$active ? '#ffffff' : '#000000')};
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition:
        background 0.2s ease,
        color 0.2s ease;

    &:hover {
        background: ${(p) => (p.$active ? '#000000' : 'rgba(0, 0, 0, 0.12)')};
    }
`;

const TabArrowBtn = styled.button`
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.08);
    cursor: pointer;
    padding: 0;
    transition: background 0.15s ease;

    &:hover {
        background: rgba(0, 0, 0, 0.16);
    }

    ${Icon} {
        width: 10px;
        height: 10px;
        fill: #1e0a3c;
    }
`;

export interface ScrollableTab {
    id: string;
    name: string;
    active: boolean;
    onClick: () => void;
}

export const ScrollableTabs: FC<{ tabs: ScrollableTab[] }> = ({ tabs }) => {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const checkArrows = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;
        setCanLeft(el.scrollLeft > 2);
        setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    }, []);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        checkArrows();
        el.addEventListener('scroll', checkArrows, { passive: true });
        const ro = new ResizeObserver(checkArrows);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', checkArrows);
            ro.disconnect();
        };
    }, [checkArrows]);

    const scroll = useCallback((dir: 'left' | 'right') => {
        scrollerRef.current?.scrollBy({ left: dir === 'left' ? -150 : 150, behavior: 'smooth' });
    }, []);

    return (
        <TabsScrollWrapper>
            {canLeft && (
                <TabArrowBtn onClick={() => scroll('left')} title='Scroll left'>
                    <Icon>
                        <ArrowLeftIcon />
                    </Icon>
                </TabArrowBtn>
            )}
            <TabsScroller ref={scrollerRef}>
                {tabs.map((t) => (
                    <TabButton key={t.id} $active={t.active} onClick={t.onClick}>
                        {T._(t.name, 'Admin')}
                    </TabButton>
                ))}
            </TabsScroller>
            {canRight && (
                <TabArrowBtn onClick={() => scroll('right')} title='Scroll right' style={{ transform: 'rotate(180deg)' }}>
                    <Icon>
                        <ArrowLeftIcon />
                    </Icon>
                </TabArrowBtn>
            )}
        </TabsScrollWrapper>
    );
};

/* ─── Component ───────────────────────────────────────────────────────────── */

interface CreativeToolLandingViewProps {
    toolId: ToolType;
    name: string;
    description: string;
    onGenerate: (images: InputImageData[], prompt: string, selections: Record<string, PresetDropdownValue | null>) => void;
    onOpenCatalog?: () => void;
    onSelectFromLibrary?: (onSelect: (imageUrl: string) => void) => void;
    videoOptions?: VideoOptions | null;
    onVideoOptionsChange?: (options: VideoOptions) => void;
    adOptions?: AdOptions;
    onAdOptionsChange?: (options: AdOptions) => void;
}

export const CreativeToolLandingView: FC<CreativeToolLandingViewProps> = ({
    toolId,
    name,
    description,
    onGenerate,
    onOpenCatalog,
    onSelectFromLibrary,
    videoOptions,
    onVideoOptionsChange,
    adOptions,
    onAdOptionsChange
}) => {
    const presetTypes = useMemo(() => getPresetTypesForTool(toolId), [toolId]);

    const [localImages, setLocalImages] = useState<InputImageData[]>([]);
    const [localPrompt, setLocalPrompt] = useState('');
    // Pre-populate dropdowns synchronously with each preset type's defaultPresetId
    // (e.g. virtual-tryon's default Model "Man 2"). PresetDropdown looks up the matching
    // preset by id from its own fetched list and renders the label + thumbnail. The component
    // is keyed by toolId at the homepage level, so a tool switch remounts and re-runs this
    // initializer with the new tool's defaults.
    const [localSelections, setLocalSelections] = useState<Record<string, PresetDropdownValue | null>>(() => {
        const initial: Record<string, PresetDropdownValue | null> = {};
        for (const t of presetTypes) {
            if (t.defaultPresetId) {
                initial[t.id] = { id: t.defaultPresetId };
            }
        }
        return initial;
    });

    const hasImage = localImages.length > 0;

    // Tool-specific generate gate. Mandatory inputs per tool:
    //  - Ad Builder       → text prompt (mandatory description of the ad).
    //  - Video Generation → animation preset (the motion to apply to the image).
    //  - Other tools      → only the input image.
    const canGenerateLocally = useMemo(() => {
        if (!hasImage) return false;
        if (toolId === 'ad-generator') return localPrompt.trim().length > 0;
        if (toolId === 'video-generation') return !!localSelections.animation;
        return true;
    }, [hasImage, toolId, localPrompt, localSelections]);

    // Multi-image-fusion (a.k.a. "Multi-Product Scene") accepts up to 5 input images;
    // every other tool replaces on each new pick.
    const supportsMultipleInputs = toolId === 'multi-image-fusion';
    const maxInputImages = 5;
    const canAddMoreImages = supportsMultipleInputs && localImages.length < maxInputImages;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [setDropdownOpen, setDropdownClose, isDropdownOpen, AddMoreDropdown] = useDropdown();
    // Counter (not a boolean) so multiple parallel uploads keep the spinner visible until
    // the last one finishes. While > 0 the AddMoreButton is replaced by a spinner.
    const [uploadingCount, setUploadingCount] = useState(0);
    const isUploading = uploadingCount > 0;

    const handleImageUpload = useCallback(
        async (file: File) => {
            setUploadingCount((c) => c + 1);
            try {
                const url = await uploadFile(file);
                const next: InputImageData = { id: `input-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`, url, name: file.name };
                setLocalImages((prev) => (supportsMultipleInputs ? [...prev, next] : [next]));
            } catch (e) {
                console.error('Upload failed:', e);
            } finally {
                setUploadingCount((c) => c - 1);
            }
        },
        [supportsMultipleInputs]
    );

    const handleLibrarySelect = useCallback(() => {
        onSelectFromLibrary?.((url) => {
            const next: InputImageData = { id: `input-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`, url, name: 'Library asset' };
            setLocalImages((prev) => (supportsMultipleInputs ? [...prev, next] : [next]));
        });
    }, [onSelectFromLibrary, supportsMultipleInputs]);

    const handleAddMoreFiles = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files ?? []);
            // Upload in parallel so the spinner stays visible only as long as the slowest file
            // takes (instead of the sum of all uploads in serial).
            await Promise.all(files.map((f) => handleImageUpload(f)));
            if (fileInputRef.current) fileInputRef.current.value = '';
        },
        [handleImageUpload]
    );

    const handleRemoveImage = useCallback((id: string) => {
        setLocalImages((prev) => prev.filter((img) => img.id !== id));
    }, []);

    const handlePresetSelect = useCallback((typeId: string, value: PresetDropdownValue | null) => {
        setLocalSelections((prev) => ({ ...prev, [typeId]: value }));
    }, []);

    const handleGenerate = useCallback(() => {
        onGenerate(localImages, localPrompt, localSelections);
    }, [localImages, localPrompt, localSelections, onGenerate]);

    return (
        <Container>
            <BlurOrb1 />
            <BlurOrb2 />
            <BlurOrb3 />

            <Content>
                <ToolTitle>
                    {T._('Start creating with', 'Admin')} <ToolTitleAccent>{T._(name, 'Admin')}</ToolTitleAccent>
                </ToolTitle>
                <ToolSubtitle>{T._(description, 'Admin')}</ToolSubtitle>

                <EmptyStatePreviewWrapper>
                    <ToolEmptyStateImage toolId={toolId} />
                </EmptyStatePreviewWrapper>

                <ControlsCard>
                    {hasImage ? (
                        <InputPreviews>
                            {localImages.map((img) => (
                                <InputPreviewItem key={img.id}>
                                    <InputPreviewImage>
                                        <img src={img.url} alt={img.name} />
                                    </InputPreviewImage>
                                    <InputPreviewRemove onClick={() => handleRemoveImage(img.id)} title={T._('Remove', 'Admin')}>
                                        <Icon>
                                            <TimesIcon />
                                        </Icon>
                                    </InputPreviewRemove>
                                </InputPreviewItem>
                            ))}
                            {canAddMoreImages && (
                                <AddMoreButton
                                    onClick={(e) => (isDropdownOpen ? setDropdownClose() : setDropdownOpen(e.currentTarget, 'top', 'left'))}
                                    disabled={isUploading}
                                    title={isUploading ? T._('Uploading...', 'Admin') : T._('Add more', 'Admin')}
                                    type='button'
                                >
                                    {isUploading ? (
                                        <InputUploadSpinner />
                                    ) : (
                                        <Icon>
                                            <PlusIcon />
                                        </Icon>
                                    )}
                                </AddMoreButton>
                            )}
                            {supportsMultipleInputs && <HiddenInput ref={fileInputRef} type='file' accept='image/*' multiple onChange={handleAddMoreFiles} />}
                        </InputPreviews>
                    ) : (
                        <ImageUploadZones onImageUpload={handleImageUpload} onOpenCatalog={onOpenCatalog} onOpenLibrary={handleLibrarySelect} />
                    )}

                    <PromptRow>
                        <PromptInput
                            value={localPrompt}
                            onChange={setLocalPrompt}
                            placeholder={T._('Describe what you want to create... (optional)', 'Admin')}
                            disabled={!hasImage}
                        />
                        {hasImage && (
                            <RoundGenerateButton onClick={handleGenerate} disabled={!canGenerateLocally} title={T._('Generate', 'Admin')}>
                                <Icon>
                                    <PaperPlaneIcon />
                                </Icon>
                            </RoundGenerateButton>
                        )}
                    </PromptRow>

                    {presetTypes.length > 0 ? (
                        <PresetSelectorsRow>
                            <PresetDropdownsGroup>
                                {presetTypes.map((typeConfig) => (
                                    <PresetDropdown
                                        key={typeConfig.id}
                                        toolId={toolId}
                                        typeConfig={typeConfig}
                                        selectedValue={localSelections[typeConfig.id] ?? null}
                                        onSelect={(value) => handlePresetSelect(typeConfig.id, value)}
                                        disabled={!hasImage}
                                        disabledTooltip={T._('Upload your product image or select a product from your Zakeke first', 'Admin')}
                                    />
                                ))}
                            </PresetDropdownsGroup>
                            <AiStudioHelpLink />
                        </PresetSelectorsRow>
                    ) : (
                        <AiStudioHelpRow>
                            <AiStudioHelpLink />
                        </AiStudioHelpRow>
                    )}
                </ControlsCard>
            </Content>

            {(toolId === 'video-generation' || toolId === 'ad-generator') && (
                <SettingsPanel
                    toolId={toolId}
                    videoOptions={videoOptions}
                    onVideoOptionsChange={onVideoOptionsChange}
                    adOptions={adOptions}
                    onAdOptionsChange={onAdOptionsChange}
                />
            )}

            {isDropdownOpen && supportsMultipleInputs && (
                <AddMoreDropdown>
                    <AddMoreMenu>
                        <AddMoreMenuItem
                            onClick={() => {
                                setDropdownClose();
                                fileInputRef.current?.click();
                            }}
                        >
                            <Icon>
                                <CloudUploadIcon />
                            </Icon>
                            {T._('Upload image', 'Admin')}
                        </AddMoreMenuItem>
                        <AddMoreMenuItem
                            onClick={() => {
                                setDropdownClose();
                                handleLibrarySelect();
                            }}
                        >
                            <Icon>
                                <FolderOpenIcon />
                            </Icon>
                            {T._('Select from library', 'Admin')}
                        </AddMoreMenuItem>
                        {onOpenCatalog && (
                            <AddMoreMenuItem
                                onClick={() => {
                                    setDropdownClose();
                                    onOpenCatalog();
                                }}
                            >
                                <Icon>
                                    <ThGridIcon />
                                </Icon>
                                {stringFormat(T._('From {0} product', 'Admin'), 'Zakeke')}
                            </AddMoreMenuItem>
                        )}
                    </AddMoreMenu>
                </AddMoreDropdown>
            )}
        </Container>
    );
};

export default CreativeToolLandingView;
